export {
	capitalizeAtPositions,
	mapToBoolean,
	replaceEmptyValue,
	toCamelCase,
	toUpperCaseSeparated,
	uncapitalizeAtPositions
};
/**
 *
 * @param str - string to change
 * @param posNumbers - zero-based array with index numbers to uppercase chars of the string
 *
 * @returns returns processed string on valid input or empty string on invalid input
 */
function capitalizeAtPositions( str: string, posNumbers: number[] ): string {
	if ( !str || !posNumbers ) { return ''; }
	const charsArray: string[] = str.split( '' );
	posNumbers.forEach( num => {
		if ( charsArray[ num ] ) {
			charsArray[ num ] = charsArray[ num ].toUpperCase();
		}
	} );
	return charsArray.join( '' );
}

/**
 * Maps a "boolean" string to a genuine boolean value, i.e. maps `'true'` to
 * `true` and `'false'` to `false`.
 * @param input - A string representing a boolean value
 * @returns A boolean corresponding to the `input` string. If this string is
 * neither `'true'` nor `'false'`, then the function will return `undefined`.
 */
function mapToBoolean( input: string ): boolean {
	const booleanMap = {
		[ 'false' ]: false,
		[ 'true' ]: true
	};
	return booleanMap[ input ];
}

/**
 * Replace empty value with spare value. Empty value is '', null or undefined.
 *
 * @param value - value to be replaced
 * @param spare - value to replace with
 * @returns returns spare value on empty input value
 */
function replaceEmptyValue( value: string, spare: string = '-' ): string {
	return value !== undefined && value !== null && value !== '' ? value : spare;
}

/**
 * Converts the string to lowerCamelCase.
 * e.g.: "[CamelCase, camel_case, CAMEL_CASE, Camel-Case]" -> "camelCase"
 *
 * @param str - string to change
 * @returns returns string as camelCase
 */
function toCamelCase( str: string ): string {
	const isPascalCase = /^[A-Z][a-zA-Z\d]+$/.test( str );
	return isPascalCase
				 ? uncapitalizeAtPositions( str, [ 0 ] )
				 : str
					 ?.toLowerCase()
					 ?.replace(
						 /[^a-zA-Z\d]+(.)/g, ( m, chr ) => chr.toUpperCase()
					 ) ?? '';
}

/**
 * Uppercase string with spaces.
 * e.g.: "[fooBar, foo_bar, foo-bar, foo_BAR, Foo_Bar, Foo-Bar]" -> "FOO BAR"
 */
function toUpperCaseSeparated( str: string ): string {
	if ( !str ) { return ''; }

	let result = str;

	const isSnakeOrKebapCase = /[_\-]+/.test( str );
	const isCamelOrPascalCase = /^(?:[A-Z]|[a-z])[a-zA-Z\d]+$/.test( str );

	if ( isSnakeOrKebapCase ) {
		result = str.replace( /[_\-]/g, ' ' )
	}
	else if ( isCamelOrPascalCase ) {
		/*
		 * Checks via RegExp for lowercase -> uppercase, lowercase -> digit,
		 * and digit -> uppercase transitions and inserts a space at each of these
		 * transitions
		 */
		result = str.replace(/([a-z])([A-Z])|([a-z])(\d)|(\d)([A-Z])/g, '$1$3$5 $2$4$6')
	}

	return result.toUpperCase();
}

/**
 *
 * @param str - string to change
 * @param posNumbers - zero-based array with index numbers to lowercase chars of the string
 *
 * @returns returns processed string on valid input or empty string on invalid input
 */
function uncapitalizeAtPositions( str: string, posNumbers: number[] ): string {
	if ( !str || !posNumbers ) { return ''; }
	const charsArray: string[] = str.split( '' );
	posNumbers.forEach( num => {
		if ( charsArray[ num ] ) {
			charsArray[ num ] = charsArray[ num ].toLowerCase();
		}
	} );
	return charsArray.join( '' );
}
